import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './DocumentViewer.css';

const DocumentViewer = ({ docUrl, isPdf, isWord, documentFilename, loading = false, initialFullscreen = false, onClose }) => {
  const { t } = useTranslation();
  const [fullscreen, setFullscreen] = useState(initialFullscreen);

  const handleClose = () => {
    setFullscreen(false);
    if (onClose) onClose();
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && fullscreen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [fullscreen]);

  if (loading) {
    return (
      <div className="document-viewer-section">
        <div className="document-viewer-loading">
          <div className="document-viewer-spinner" />
          <p>{t('common.loadingDocument', 'Loading document...')}</p>
        </div>
      </div>
    );
  }

  if (!docUrl) {
    return null;
  }

  return (
    <>
      {!initialFullscreen && (
        <div className="document-viewer-section">
          <h2 className="document-viewer-title">
            {t('common.attachedDocument', 'Attached Document')}
          </h2>

          <div className="document-viewer-preview">
            {isPdf ? (
              <iframe
                src={docUrl}
                className="document-viewer-iframe"
                title={t('common.pdfPreview', 'PDF Preview')}
              />
            ) : isWord ? (
              <div className="document-viewer-word-notice">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6M9 16h6" />
                </svg>
                <p>{t('common.wordDocumentNotice', 'Word documents cannot be previewed in the browser. Please download the file to view it.')}</p>
              </div>
            ) : (
              <img
                src={docUrl}
                alt={t('common.attachedDocument', 'Attached Document')}
                className="document-viewer-image"
              />
            )}

            <div className="document-viewer-actions">
              {!isWord && (
                <button
                  onClick={() => setFullscreen(true)}
                  className="document-viewer-button document-viewer-fullscreen-button"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  {t('common.viewFullscreen', 'View Fullscreen')}
                </button>
              )}

              <a
                href={docUrl}
                download={documentFilename || 'document'}
                className="document-viewer-button document-viewer-download-button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('common.download', 'Download')}
              </a>
            </div>
          </div>
        </div>
      )}

      {fullscreen && docUrl && (
        <div className="document-viewer-fullscreen-modal" onClick={handleClose}>
          <div className="document-viewer-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <div className="document-viewer-fullscreen-header">
              <button
                onClick={handleClose}
                className="document-viewer-close-button"
                aria-label={t('common.close', 'Close')}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="document-viewer-fullscreen-viewer">
              {isPdf ? (
                <iframe
                  src={docUrl}
                  className="document-viewer-iframe-fullscreen"
                  title={t('common.pdfPreview', 'PDF Preview')}
                />
              ) : isWord ? (
                <div className="document-viewer-word-notice">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6M9 16h6" />
                  </svg>
                  <p>{t('common.wordDocumentNotice', 'Word documents cannot be previewed in the browser. Please download the file to view it.')}</p>
                </div>
              ) : (
                <img
                  src={docUrl}
                  alt={t('common.attachedDocument', 'Attached Document')}
                  className="document-viewer-image-fullscreen"
                />
              )}
            </div>

            <div className="document-viewer-fullscreen-actions">
              <a
                href={docUrl}
                download={documentFilename || 'document'}
                className="document-viewer-button document-viewer-download-button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('common.download', 'Download')}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentViewer;
